/**
 * What a customer may add to their own bid, above the Accept button (owner, Sep 19 2026):
 *   - bed weed spraying, at the price the owner set
 *   - a lawn plan — or a bigger one than the office put on the bid — highest first, each with its whole year
 *   - an extra application inside the plan (an extra weed spray, an extra fertilizer): its yearly price off the
 *     cost sheet, spread over twelve months
 *
 * The three plans are one radio group: the whole card is the control, and exactly one reads as chosen. Before this
 * the card already on the bid showed "On your bid ✓" and tapping it did nothing at all, which is what made picking
 * a plan look broken. The extras for the chosen plan sit under all three rather than growing inside one of them,
 * so the cards stay comparable.
 *
 * It never asks the size of the lawn or the grass: the server already priced everything for this yard
 * (services/bidPage). Every number shown comes from the server — this only adds them up for the Accept button —
 * and the server prices the choices again when they accept.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Minus, Plus, Loader, Leaf, Sparkles } from 'lucide-react';
import BidPlanYear, { PlanPicture, PlanShift } from './BidPlanYear';

type AddOn = { key: string; name: string; blurb: string; price: number; cadence: string; cadence_label: string; per: string; points: string[] };
type ExtraService = { service: string; word: string; label?: string; common?: boolean; help: string; per_year: number; about_monthly: number; max_extra: number };
export type BidExtrasData = {
  addons: AddOn[];
  /** null: the lawn plans are switched off on this bid (quotes.offer_plans) — only the add-on is offered. */
  plan: null | {
    // mowed_monthly: the owner's price for the plan on the bid if we also mow the yard (Oct 14 2026) — only on a yard we do not mow yet.
    on_bid: { plan: string; monthly: number; items: Record<string, number>; mowed_monthly?: number | null } | null;
    plans: PlanPicture[]; services: ExtraService[]; how_a_visit_works?: string; pick_any?: boolean;
    /** Whether we mow the yard. A yard we do not mow is never told its applications ride a mow. */
    mowed?: boolean;
    start_options?: { default: string; options: { start: string; label: string; kind: string; words: string }[] } | null;
  };
};
export type BidChoices = { plan: string | null; items: Record<string, number> | null; addons: string[]; start: string | null; shifts: PlanShift[] };
export type BidDelta = { monthly: number; visit: number; once: number };

const money = (n: number) => `$${(Math.round(Number(n) * 100) / 100).toFixed(2).replace(/\.00$/, '')}`;

export default function BidExtras({ extras, price, onChange }: {
  extras: BidExtrasData;
  /** Ask the server what a plan with these applications costs for this yard. */
  price: (choice: { plan: string; items: Record<string, number> }) => Promise<PlanPicture | null>;
  onChange: (choices: BidChoices, delta: BidDelta) => void;
}) {
  const planInfo = extras.plan;   // null: the plans are switched off on this bid — nothing plan-shaped is drawn
  const plans = useMemo(() => planInfo?.plans ?? [], [planInfo]);
  const services = planInfo?.services ?? [];
  const startOptions = planInfo?.start_options ?? null;
  const howWorks = planInfo?.how_a_visit_works;
  const onBid = planInfo?.on_bid ?? null;
  // A lawn-plan bid IS the plan offer, so every plan is a straight pick — not an upgrade on something agreed.
  const pickAny = !!planInfo?.pick_any;
  const mowed = planInfo?.mowed !== false;
  const [addons, setAddons] = useState<Set<string>>(new Set());
  const [plan, setPlan] = useState<string | null>(onBid?.plan || (pickAny ? 'plan' : null));
  const [more, setMore] = useState<Record<string, number>>({});          // extra applications, per service
  const [picture, setPicture] = useState<PlanPicture | null>(null);        // the chosen plan with the extras, priced by the server
  const [pricing, setPricing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [openYear, setOpenYear] = useState<string | null>(null);
  const [openAddon, setOpenAddon] = useState<string | null>(null);
  const [allExtras, setAllExtras] = useState(false);                        // the rarer applications sit behind "More options"
  const [shifts, setShifts] = useState<PlanShift[]>([]);
  const [start, setStart] = useState<string | null>(startOptions?.default || null);
  const seq = useRef(0);

  const card = useMemo(() => plans.find(p => p.plan === plan) || null, [plans, plan]);
  const hasMore = Object.values(more).some(n => n > 0);
  // The plan's own counts (what the office put in it, when it is the one on the bid) plus what they added.
  const baseItems = useMemo(() => (plan && onBid && onBid.plan === plan ? onBid.items : card?.items) || {}, [plan, onBid, card]);
  const items = useMemo(() => {
    const out: Record<string, number> = { ...baseItems };
    for (const [s, n] of Object.entries(more)) if (n > 0) out[s] = (Number(baseItems[s]) || 0) + n;
    return out;
  }, [baseItems, more]);

  // Re-price with the server whenever the extras change.
  useEffect(() => {
    if (!plan || !hasMore) { setPicture(null); setPricing(false); setProblem(null); return; }
    const mine = ++seq.current;
    setPricing(true); setProblem(null);
    const t = setTimeout(async () => {
      try {
        const p = await price({ plan, items });
        if (mine !== seq.current) return;
        if (p) setPicture(p); else setProblem('We could not price that extra just now. Your plan is still yours. Text us and we will add the extra for you.');
      } catch { if (mine === seq.current) setProblem('We could not price that extra just now. Your plan is still yours. Text us and we will add the extra for you.'); }
      finally { if (mine === seq.current) setPricing(false); }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, JSON.stringify(items), hasMore]);

  // While the server prices an extra application, keep showing the plan's own price rather than nothing: this used
  // to drop the whole plan off the Accept button for the length of the round trip.
  const planMonthly = plan ? (hasMore ? (picture?.monthly ?? card?.monthly ?? null) : (card?.monthly ?? null)) : null;
  const planChanged = !!plan && (!onBid || onBid.plan !== plan || hasMore);
  const planDelta = planChanged && planMonthly !== null ? planMonthly - (onBid?.monthly || 0) : 0;

  // Tell the page: what to send when they accept, and what it adds to the numbers on the button.
  useEffect(() => {
    const picked = extras.addons.filter(a => addons.has(a.key));
    const delta: BidDelta = { monthly: planDelta, visit: 0, once: 0 };
    for (const a of picked) { if (a.cadence === 'mow:1') delta.visit += a.price; else if (a.cadence === 'once') delta.once += a.price; else if (a.cadence === 'month:1') delta.monthly += a.price; }
    // The plan they tapped always goes: they saw its price on the card and the server prices it again on accept.
    // On a lawn-plan bid the chosen plan is the whole bid, so it is sent even when it matches the recommended default.
    // Extra applications only go once a price for them has actually been shown — never on a failed or pending one.
    onChange({
      plan: pickAny ? plan : (planChanged ? plan : null),
      items: planChanged && hasMore && !problem && !pricing && picture ? items : null,
      addons: picked.map(a => a.key),
      // The month the plan starts goes with any plan they picked (the server keeps it on the plan line on a project or
      // mowing bid); sliding the year is only offered on a lawn-plan bid.
      start: plan ? start : null,
      shifts: pickAny ? shifts : [],
    }, delta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addons, plan, planDelta, planChanged, hasMore, pricing, problem, JSON.stringify(items), start, JSON.stringify(shifts)]);

  // Already their plan: tapping it again keeps it. It used to fall back to whatever the office put on the bid, so on
  // a plan bid the tap did nothing visible at all (owner, Sep 19 2026: "pick a plan doesn't work").
  const pickPlan = (key: string) => {
    const next = plan === key ? (pickAny ? key : (onBid ? onBid.plan : null)) : key;
    if (next !== plan) { setMore({}); setPicture(null); setShifts([]); }   // their extras were priced against the plan being left
    setPlan(next);
  };
  // Arrow keys move between the three, the way a radio group should.
  const moveChoice = (from: string, by: number) => {
    const keys = plans.map(p => p.plan);
    const i = keys.indexOf(from);
    if (i < 0) return;
    const next = keys[(i + by + keys.length) % keys.length];
    pickPlan(next);
    // Focus follows the choice, so the ring is never left behind on a card that is no longer theirs.
    setTimeout(() => { const el = document.querySelector(`[data-plan="${next}"]`) as HTMLElement | null; el?.focus(); }, 0);
  };
  const bump = (service: string, by: number, max: number) => { setShifts([]); setMore(prev => ({ ...prev, [service]: Math.max(0, Math.min(max, (prev[service] || 0) + by)) })); };

  const nothing = extras.addons.length === 0 && plans.length === 0;
  if (nothing) return null;

  const planCards = plans.length > 0 ? (
    <div className="space-y-3">
      {!pickAny && (
        <div className="flex items-center gap-2">
          <Leaf className="h-4 w-4 text-emerald-600" />
          <p className="text-[15px] font-bold text-gray-900">{onBid ? 'Your lawn plan. Upgrade it or add to it' : 'Add a lawn treatment plan'}</p>
        </div>
      )}
      {!onBid && <p className="text-sm text-gray-600 -mt-1">Fertilizer and weed control done on the mowing visits we are already making. One flat price a month, priced for your lawn.</p>}

      {/* Three cards that read the same way, with one obvious chosen state. The whole card is the control;
          "See the whole year" sits inside it and must not change the choice. */}
      <div role="radiogroup" aria-label="Lawn plan" className="space-y-2.5">
        {plans.map(p => {
          const chosen = plan === p.plan;
          const shown = chosen && hasMore && picture ? picture : p;
          const yearOpen = openYear === p.plan;
          // The owner's price if we also mow (Oct 14 2026): on the plan as sent, its own; while they add to it, his
          // price carried onto what they added (the server works it out with the price).
          const ifMowed = shown === p ? (onBid && p.plan === onBid.plan ? onBid.mowed_monthly || 0 : 0) : shown.mowed_monthly || 0;
          return (
            <div key={p.plan} className={`rounded-2xl border-2 transition-colors ${chosen ? 'border-brand-600 bg-brand-50/40 shadow-brand' : 'border-gray-200 bg-white shadow-soft'}`}>
              <div role="radio" data-plan={p.plan} aria-checked={chosen} tabIndex={chosen || (!plan && p.plan === plans[0].plan) ? 0 : -1}
                onClick={() => pickPlan(p.plan)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pickPlan(p.plan); }
                  if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); moveChoice(p.plan, 1); }
                  if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); moveChoice(p.plan, -1); }
                }}
                className="press px-4 pt-3.5 pb-3 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 rounded-2xl">
                <div className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-widest text-gray-600">{p.kicker}</span>
                    <span className="block text-lg font-bold text-gray-900 leading-tight">{p.name}</span>
                  </span>
                  <span className="text-right flex-shrink-0">
                    <span className="block text-2xl font-bold text-gray-900 tabular-nums leading-none">{money(shown.monthly)}<span className="text-xs font-medium text-gray-500">/mo</span></span>
                    <span className="block text-[11px] text-gray-500 mt-1">{shown.applications} applications a year</span>
                    {/* Plain words on the plan it is for, never a button. */}
                    {ifMowed > 0 && (
                      <span className="block text-[11px] font-medium text-gray-700 mt-0.5">{money(ifMowed)}/mo if we also mow your yard</span>
                    )}
                  </span>
                </div>
                {p.tag && <p className="text-sm text-gray-600 mt-1.5">{p.tag}</p>}
                {(p.includes || []).length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {p.includes.map(w => (
                      <li key={w} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />{w}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-3">
                  <span className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold border-2 ${chosen ? 'border-brand-600 bg-brand-600 text-black shadow-brand' : 'border-brand-600 bg-brand-50 text-brand-700'}`}>
                    <span className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${chosen ? 'border-white bg-white' : 'border-brand-500 bg-white'}`}>
                      {chosen && <Check className="h-3 w-3 text-brand-600" strokeWidth={3} />}
                    </span>
                    {chosen ? 'Your plan' : `Choose ${p.name}`}
                  </span>
                </div>
              </div>
              {/* Its own row, outside the radio: a control nested inside a radio is announced as its content, and it
                  used to wrap differently on each card so the three were different heights. */}
              <div className="px-4 pb-3 -mt-1">
                <button type="button" onClick={() => setOpenYear(yearOpen ? null : p.plan)} aria-expanded={yearOpen}
                  className="inline-flex items-center gap-1 text-xs font-medium text-brand-700">
                  {yearOpen ? 'Hide the year' : 'See the whole year'} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${yearOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
              {yearOpen && <div className="px-4 pb-4 border-t border-gray-100 pt-3"><BidPlanYear plan={shown} how={howWorks} mowed={mowed} /></div>}
            </div>
          );
        })}
      </div>

      {pickAny && plan && card && (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5 space-y-3">
          <p className="text-sm font-semibold text-gray-900">Adjust timing</p>
          <p className="text-xs text-gray-500 -mt-2">When the year starts, and a month that does not work. Same treatments, same price.</p>
          {startOptions?.options?.length ? (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-gray-600 mb-1.5">Start the year</p>
              <div className="flex flex-wrap gap-2">
                {startOptions.options.map(o => {
                  const on = start === o.start;
                  return (
                    <button key={o.start} type="button" onClick={() => setStart(o.start)}
                      className={`press px-3.5 py-2 rounded-xl text-sm font-semibold border-2 ${on ? 'border-brand-600 bg-brand-600 text-black shadow-brand' : 'border-gray-200 bg-white text-gray-800'}`}>
                      {o.words}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          {/* Sliding a month MERGES it into the one it lands on, and until now
              there was no way back out (owner, Sep 21 2026: "if I move one to a
              month then they all combine, no way to uncombine"). Undo takes the
              last slide off; Start over clears them all. */}
          <BidPlanYear plan={(hasMore && picture) ? picture : card} how={howWorks} mowed={mowed} shifts={shifts}
            onShift={(from, dir) => setShifts(prev => [...prev, { from, dir }])}
            onUndo={shifts.length ? () => setShifts(prev => prev.slice(0, -1)) : undefined}
            onReset={shifts.length ? () => setShifts([]) : undefined} />
        </div>
      )}

      {/* On a project or mowing bid that offers the plans: the month the plan starts, nothing else about the year.
          Owner, Oct 29 2026: Chris Land wanted the plan from February and could only say so by text. */}
      {!pickAny && plan && card && startOptions?.options?.length ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
          <p className="text-sm font-semibold text-gray-900">When should the plan start?</p>
          <p className="text-xs text-gray-500 mb-2">Same treatments, same price — the year just starts in the month you pick.</p>
          <div className="flex flex-wrap gap-2">
            {startOptions.options.map(o => {
              const on = start === o.start;
              return (
                <button key={o.start} type="button" onClick={() => setStart(o.start)}
                  className={`press px-3.5 py-2 rounded-xl text-sm font-semibold border-2 ${on ? 'border-brand-600 bg-brand-600 text-black shadow-brand' : 'border-gray-200 bg-white text-gray-800'}`}>
                  {o.words}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Extras for whichever plan is chosen — once, under all three, so the cards stay comparable instead of one
          of them growing a long panel in the middle of the stack. */}
      {plan && card && services.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-amber-500" /> Want more of something in {card.name}?</p>
          <p className="text-xs text-gray-500 mt-0.5">Add an extra application to your year. Its price is spread over twelve months and added to the plan.</p>
          <ul className="mt-2.5 divide-y divide-gray-100">
            {services.filter(s => allExtras || s.common || (more[s.service] || 0) > 0 || !services.some(x => x.common)).map(s => {
              const n = more[s.service] || 0;
              return (
                <li key={s.service} className="py-2.5 flex items-center gap-3">
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 first-letter:uppercase">Extra {s.label || s.word}</span>
                    <span className="block text-xs text-gray-500">{s.help}</span>
                    <span className="block text-xs font-medium text-brand-700 tabular-nums mt-0.5">+about {money(Math.max(1, Math.round(s.about_monthly)))}/mo each</span>
                  </span>
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    <button type="button" onClick={() => bump(s.service, -1, s.max_extra)} disabled={n === 0} aria-label={`One less extra ${s.word}`} className="press h-11 w-11 rounded-xl border-2 border-gray-200 flex items-center justify-center text-gray-700 disabled:opacity-30"><Minus className="h-4 w-4" /></button>
                    <span className="w-5 text-center text-sm font-semibold tabular-nums">{n}</span>
                    <button type="button" onClick={() => bump(s.service, 1, s.max_extra)} disabled={n >= s.max_extra} aria-label={`One more extra ${s.word}`} className="press h-11 w-11 rounded-xl border-2 border-brand-300 text-brand-700 flex items-center justify-center disabled:opacity-30"><Plus className="h-4 w-4" /></button>
                  </span>
                </li>
              );
            })}
          </ul>
          {services.some(s => !s.common) && services.some(s => s.common) && (
            <button type="button" onClick={() => setAllExtras(v => !v)} aria-expanded={allExtras} className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-700">
              {allExtras ? 'Fewer options' : 'More options: grub, fungus, insect, iron'} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${allExtras ? 'rotate-180' : ''}`} />
            </button>
          )}
          {hasMore && (
            <p className="mt-2 text-sm text-gray-800 flex items-center gap-2">
              {pricing ? <><Loader className="h-4 w-4 animate-spin text-gray-600" /> Pricing that for your lawn…</>
                : problem ? <span className="text-amber-700">{problem}</span>
                : picture ? <span>With your extras, {card.name} is <b className="tabular-nums">{money(picture.monthly)} a month</b>{picture.base_monthly ? <span className="text-gray-500"> (was {money(onBid && onBid.plan === plan ? onBid.monthly : picture.base_monthly)})</span> : null}.</span> : null}
            </p>
          )}
        </div>
      )}
    </div>
  ) : null;

  const addonCards = extras.addons.map(a => {
    const on = addons.has(a.key);
    const showing = openAddon === a.key;
    return (
      <div key={a.key} className={`rounded-2xl border-2 transition-colors ${on ? 'border-brand-600 bg-brand-50/60' : 'border-gray-200 bg-white'}`}>
        <button type="button" aria-pressed={on} onClick={() => setAddons(prev => { const n = new Set(prev); if (n.has(a.key)) n.delete(a.key); else n.add(a.key); return n; })}
          className="press w-full flex items-center gap-3 px-4 py-3.5 text-left rounded-2xl">
          <span className={`h-6 w-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 ${on ? 'bg-brand-600 border-brand-600' : 'border-gray-300 bg-white'}`}>{on && <Check className="h-4 w-4 text-white" />}</span>
          <span className="flex-1 min-w-0">
            <span className="block text-[15px] font-semibold text-gray-900">{a.name}</span>
            <span className="block text-sm text-gray-600">{a.blurb || `Done ${a.cadence_label}, on the mow.`}</span>
          </span>
          <span className="text-right flex-shrink-0">
            <span className="block text-lg font-bold text-gray-900 tabular-nums">+{money(a.price)}<span className="text-xs font-medium text-gray-500">{a.per}</span></span>
            <span className="block text-[11px] text-gray-500">{a.cadence_label}</span>
          </span>
        </button>
        {a.points.length > 0 && (
          <div className="px-4 pb-3 -mt-1">
            <button type="button" onClick={() => setOpenAddon(showing ? null : a.key)} aria-expanded={showing} className="inline-flex items-center gap-1 text-xs font-medium text-brand-700">
              {showing ? 'Hide the details' : 'What you get'} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showing ? 'rotate-180' : ''}`} />
            </button>
            {showing && (
              <ul className="mt-2 space-y-1.5">
                {a.points.map(pt => <li key={pt} className="flex items-start gap-2 text-sm text-gray-700"><Check className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />{pt}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  });

  return (
    <section aria-label="Add to your bid" className="space-y-5">
      <div>
        <p className="text-[11px] uppercase tracking-widest text-gray-600">{pickAny ? 'Your plan' : 'Make it yours'}</p>
        <p className="text-lg font-bold text-gray-900 mt-0.5">{pickAny ? 'Which plan do you want?' : 'Want anything else done while we are there?'}</p>
        <p className="text-sm text-gray-500">{pickAny ? 'Tap the one you want. The price at the bottom follows, and nothing is charged by signing.' : 'Tap to add. The total on the button updates as you go, and nothing is charged by accepting.'}</p>
      </div>

      {/* On a lawn-plan bid the plan is the whole point, so it leads; anywhere else the plan is an addition. */}
      {pickAny && planCards}
      {addonCards}
      {!pickAny && planCards}
    </section>
  );
}
